import React, { useEffect, useContext, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import PropTypes from 'prop-types';

import {
    DialogBody,
    Heading,
    Text,
} from '@zillow/constellation';

import Constants from 'app/shared/constants';
import { PropertiesSelectors } from 'app/shared/selectors';
import { RMWContext } from 'app/shared/providers/RMWContext';
import AddPropertyWrapper from 'app/shared/components/AddPropertyWrapper';
import AnalyticsConstants from 'app/shared/constants/AnalyticsConstants';
import analyticsUtils from 'app/client/utils/analyticsUtils';
import ModalActions from 'app/shared/actions/ModalActions';
import pathsManifest from 'app/shared/pathsManifest';
import PostingActions from 'app/shared/actions/PostingActions';
import PropertiesActions from 'app/shared/actions/PropertiesActions';
import propertyUtils from 'app/shared/utils/propertyUtils';
import TrackingEventsConstants from 'app/shared/constants/TrackingEventsConstants';
import UserActions from 'app/shared/actions/UserActions';

import ActionButtons from './components/ActionButtons';
import ArchiveProperty from './components/ArchiveProperty';

import { StyledModalDialog } from './StyledComponents';

const { formatAddress } = propertyUtils;
const { property: { unArchive, archive } } = PostingActions;
const { updateCurrentProperty } = PropertiesActions;
const {
    GA_EVENTS: {
        UNARCHIVE_LISTING,
        ARCHIVE_LISTING,
    },
} = AnalyticsConstants;

const pathsOptions = pathsManifest.getAllPaths();

const propTypes = {
    behavior: PropTypes.oneOf(['Add', 'Edit']),
    destinationTab: PropTypes.oneOf([
        pathsOptions.propertyDetailsListing,
        pathsOptions.propertyDetailsApplications,
        pathsOptions.propertyDetailsPayments,
        pathsOptions.propertyDetailsLeases,
    ]),
    isOpen: PropTypes.bool,
    preventLandingPageRedirect: PropTypes.bool,
    skipEditStage: PropTypes.bool,
};

const defaultProps = {
    behavior: 'Add',
    destinationTab: pathsOptions.propertyDetailsListing,
    isOpen: false,
    preventLandingPageRedirect: false,
    skipEditStage: false,
};

const AddPropertyModal = ({
    behavior,
    destinationTab,
    isOpen,
    preventLandingPageRedirect,
    skipEditStage,
    totalListings,
}) => {
    const dispatch = useDispatch();
    const { paths } = useContext(RMWContext);
    const [isUserAtArchiveStep, setIsUserAtArchiveStep] = useState(skipEditStage);
    const [errorAtArchive, setErrorAtArchive] = useState(false);
    const currentProperty = useSelector(state => PropertiesSelectors.makeCurrentProperty(state));
    const {
        archived,
        isArchived,
        isAddressValid,
        listingAlias,
    } = currentProperty;

    useEffect(() => {
        if (behavior === 'Add' && isOpen) {
            dispatch(UserActions.trackEvent({
                trackingEvent: TrackingEventsConstants.OPEN_ADD_PROPERTY_MODAL,
            }));
        }
    }, [isOpen]);

    const handleCancelArchive = () => {
        setIsUserAtArchiveStep(false);
        setErrorAtArchive(false);
    };

    const modalClose = (createdListing = false, isModalDismissed = false) => {
        if (!preventLandingPageRedirect) {
            // if zero listings and didn't just create one, send back to landing page
            if (totalListings === 0 && !createdListing) {
                window.router.transitionTo(paths.base);
            }
        }

        if (isModalDismissed) {
            dispatch(analyticsUtils.track.GAEventV2({
                ...AnalyticsConstants.GA_EVENTS.ADD_PROPERTY_MODAL_DISMISS,
                ...(listingAlias) && { label: listingAlias },
            }));
        }
        dispatch(ModalActions.updateDisplayAddPropertyModal({ isOpen: false }));
        handleCancelArchive();
        dispatch(ModalActions.hideModal());
    };

    const handleArchiveOrUnArchive = async () => {
        if (!isUserAtArchiveStep) {
            setIsUserAtArchiveStep(true);
        } else {
            try {
                const { success } = await dispatch(
                    (archived || isArchived) ? unArchive(listingAlias) : archive(listingAlias)
                );
                if (success) {
                    const gaEvent = archived ? UNARCHIVE_LISTING : ARCHIVE_LISTING;
                    const label = listingAlias && { label: listingAlias };

                    if (archived || isArchived) {
                        dispatch(PostingActions.property.getDetails(listingAlias));
                        dispatch(updateCurrentProperty({ archived: false, isArchived: false }));
                        dispatch({
                            type: Constants.UPDATE_PROPERTY_OVERVIEW,
                            payload: { isArchived: false },
                        });
                    } else {
                        dispatch(updateCurrentProperty({ archived: true, isArchived: true }));
                        dispatch({
                            type: Constants.UPDATE_PROPERTY_OVERVIEW,
                            payload: { isArchived: true },
                        });
                    }

                    dispatch(analyticsUtils.track.GAEventV2({ ...gaEvent, ...label }));
                    modalClose();
                }
            } catch (err) {
                setErrorAtArchive(true);
            }
        }
    };


    return (
        <StyledModalDialog
            className={(isAddressValid === false) && 'ModalDialog--disabled'}
            data-test="add-property-modal"
            scrollDocument={true}
            shouldCloseOnOutsideClick={isAddressValid}
            shouldCloseOnESCKeyPress={isAddressValid}
            wide={true}
            isOpen={isOpen}
            header={(<Heading level={6}>{`${behavior} property`}</Heading>)}
            onClose={() => modalClose(false, true)}
            mediaQuery={{ xs: { fullScreen: true }, lg: { fullScreen: false } }}
        >
            <DialogBody>
                {isUserAtArchiveStep ? (
                    <ArchiveProperty
                        onCancel={handleCancelArchive}
                        actionHandler={handleArchiveOrUnArchive}
                        address={formatAddress(currentProperty).formattedCompleteAddress}
                        isArchived={archived || isArchived}
                        errorAtArchive={errorAtArchive}
                    />
                ) : (
                    <div>
                        {behavior === 'Add' && (
                            <Text fontType="h6" as="div">
                                Please provide your property&apos;s info.
                            </Text>
                        )}
                        <AddPropertyWrapper
                            behavior={behavior}
                            onCustomerSupportClick={modalClose}
                            onSubmitSuccess={modalClose}
                            ActionButtonComponent={ActionButtons}
                            // ActionButton additional props
                            onModalClose={modalClose}
                            handleArchiveOrUnArchive={handleArchiveOrUnArchive}
                            destinationTab={destinationTab}
                        />
                    </div>
                )}
            </DialogBody>
        </StyledModalDialog>
    );
};

AddPropertyModal.propTypes = propTypes;
AddPropertyModal.defaultProps = defaultProps;

export default AddPropertyModal;
